import { useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '../api/client.js';
import type { NotificationsResponse } from '../api/types.js';
import { useSession } from '../auth/SessionProvider.js';
import { PERMISSIONS } from '../../shared/permissions.js';
import { APP_MARK, APP_NAME } from '../lib/branding.js';
import { Button, classes } from './ui.js';
import { NotificationPanel } from './NotificationPanel.js';

interface NavItem {
  to: string;
  label: string;
  show: boolean;
}

export function AppLayout() {
  const { user, signOut, can, canAny, isContractor } = useSession();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  // Polled rather than pushed: notifications are in-app only in this version, and a
  // 60-second interval is well within what the workflow needs.
  const { data: notifications } = useQuery({
    queryKey: ['notifications', 'summary'],
    queryFn: () =>
      apiRequest<NotificationsResponse>('/api/notifications', { query: { limit: 20 } }),
    refetchInterval: 60_000,
    enabled: Boolean(user),
  });

  const unreadCount = notifications?.unreadCount ?? 0;

  const navItems: NavItem[] = [
    { to: '/', label: 'Dashboard', show: true },
    { to: '/work-orders', label: 'Work orders', show: can(PERMISSIONS.WORK_ORDER_VIEW) },
    {
      to: '/approvals',
      label: 'Awaiting my approval',
      show: can(PERMISSIONS.PROPOSAL_APPROVE),
    },
    { to: '/reports', label: 'Reports', show: can(PERMISSIONS.REPORT_VIEW) },
    {
      to: '/admin',
      label: 'Administration',
      show: canAny(
        PERMISSIONS.ADMIN_USER_MANAGE,
        PERMISSIONS.ADMIN_FACILITY_MANAGE,
        PERMISSIONS.ADMIN_TASK_ORDER_MANAGE,
        PERMISSIONS.ADMIN_CATEGORY_MANAGE,
      ),
    },
    {
      to: '/employee-documents',
      label: 'Employee documents',
      show: canAny(
        PERMISSIONS.EMPLOYEE_DOCS_REVIEW,
        PERMISSIONS.EMPLOYEE_DOCS_MANAGE,
        PERMISSIONS.EMPLOYEE_DOCS_VIEW_RELEASED,
      ),
    },
    { to: '/audit-log', label: 'Audit log', show: can(PERMISSIONS.AUDIT_VIEW) },
    { to: '/help', label: 'Guides', show: true },
  ].filter((item) => item.show);

  const handleSignOut = async () => {
    await signOut();
    navigate('/sign-in', { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-50 focus:rounded focus:bg-white focus:px-3 focus:py-2 focus:text-sm focus:font-semibold focus:text-brand-800 focus:shadow"
      >
        Skip to main content
      </a>

      {/* Printing a page is how a document leaves the platform, so the chrome goes. */}
      <header className="border-b border-slate-200 bg-white print:hidden">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2.5">
              <span className="grid size-9 place-items-center rounded bg-brand-700 text-sm font-bold text-white">
                {APP_MARK}
              </span>
              <span className="hidden flex-col leading-tight sm:flex">
                <span className="text-sm font-semibold text-slate-900">{APP_NAME}</span>
                <span className="text-xs text-slate-500">
                  {isContractor ? 'VGS' : (user?.organizationCode ?? '')} portal
                </span>
              </span>
            </Link>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setNotificationsOpen(true)}
              className="relative rounded-md p-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              aria-label={
                unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'
              }
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="size-5" aria-hidden="true">
                <path d="M10 2a6 6 0 0 0-6 6v2.586l-.707.707A1 1 0 0 0 4 13h12a1 1 0 0 0 .707-1.707L16 10.586V8a6 6 0 0 0-6-6ZM8.05 15a2 2 0 0 0 3.9 0h-3.9Z" />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 grid min-w-4 place-items-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            <div className="hidden items-center gap-3 sm:flex">
              <div className="text-right leading-tight">
                <p className="text-sm font-medium text-slate-900">{user?.fullName}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
              <Button variant="secondary" onClick={() => void handleSignOut()}>
                Sign out
              </Button>
            </div>

            <button
              type="button"
              className="rounded-md p-2 text-slate-600 hover:bg-slate-100 sm:hidden"
              onClick={() => setMenuOpen((previous) => !previous)}
              aria-expanded={menuOpen}
              aria-label="Toggle navigation menu"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="size-5" aria-hidden="true">
                <path d="M2 5.75A.75.75 0 0 1 2.75 5h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 5.75Zm0 4.25a.75.75 0 0 1 .75-.75h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 10Zm.75 3.5a.75.75 0 0 0 0 1.5h14.5a.75.75 0 0 0 0-1.5H2.75Z" />
              </svg>
            </button>
          </div>
        </div>

        <nav aria-label="Main" className="mx-auto max-w-7xl px-4 sm:px-6">
          <ul
            className={classes(
              'flex-col gap-1 pb-3 sm:flex sm:flex-row sm:gap-1',
              menuOpen ? 'flex' : 'hidden sm:flex',
            )}
          >
            {navItems.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    classes(
                      'block rounded-md px-3 py-2 text-sm font-medium',
                      isActive
                        ? 'bg-brand-50 text-brand-800'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                    )
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
            <li className="mt-2 border-t border-slate-200 pt-2 sm:hidden">
              <button
                type="button"
                onClick={() => void handleSignOut()}
                className="block w-full rounded-md px-3 py-2 text-left text-sm font-medium text-slate-600 hover:bg-slate-100"
              >
                Sign out ({user?.email})
              </button>
            </li>
          </ul>
        </nav>
      </header>

      <main id="main-content" className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
        <Outlet />
      </main>

      <NotificationPanel open={notificationsOpen} onClose={() => setNotificationsOpen(false)} />
    </div>
  );
}
